import React, { useState } from "react";
import {
  SCENARIOS,
  STARTING_DEBT,
  STARTING_EMERGENCY_SAVINGS,
  EMERGENCY_FUND_GOAL,
} from "./data.js";

const POOL_LABELS = {
  essentials: "Essentials",
  discretionary: "Discretionary",
  sinkingFund: "Sinking Fund",
  emergencyFund: "Emergency Fund",
  lifestyle: "Lifestyle",
};

function Bar({ label, value, max, accent = "cyan", suffix = "" }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, Math.round((value / max) * 100))) : 0;
  const accentBg = {
    cyan: "bg-cyan",
    lime: "bg-lime",
    gold: "bg-gold",
    magenta: "bg-magenta",
    danger: "bg-danger",
  }[accent];

  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between text-[9px] text-mist/60">
        <span>{label}</span>
        <span>
          ${Math.max(0, Math.round(value)).toLocaleString()}
          {suffix}
        </span>
      </div>
      <div className="h-1.5 w-full border border-black/60 bg-ink">
        <div className={`h-full ${accentBg} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function CampaignStage({ plan, onComplete }) {
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [resolved, setResolved] = useState(null); // { choice, scenario }
  const [log, setLog] = useState([]);

  const [pools, setPools] = useState({
    essentials: plan.essentials,
    discretionary: plan.discretionary,
    sinkingFund: plan.sinkingFund,
    lifestyle: plan.lifestyle,
  });
  const [emergencyFundBalance, setEmergencyFundBalance] = useState(
    STARTING_EMERGENCY_SAVINGS + plan.emergencyFund
  );
  const [debtBalance, setDebtBalance] = useState(Math.max(0, STARTING_DEBT - plan.debtPayoff));
  const [goalsBalance, setGoalsBalance] = useState(plan.goals);
  const [debtIncurred, setDebtIncurred] = useState(0);

  const scenario = SCENARIOS[scenarioIndex];
  const allDone = scenarioIndex >= SCENARIOS.length;

  const canAfford = (choice) => {
    if (choice.pool === "credit" || choice.pool === null || choice.pool?.startsWith("windfall")) return true;
    if (choice.pool === "emergencyFund") return emergencyFundBalance >= choice.cost;
    return (pools[choice.pool] ?? 0) >= choice.cost;
  };

  const applyChoice = (choice) => {
    if (choice.pool === "credit") {
      setDebtBalance((d) => d + choice.cost);
      setDebtIncurred((d) => d + choice.cost);
    } else if (choice.pool === "emergencyFund") {
      setEmergencyFundBalance((b) => b - choice.cost);
    } else if (choice.pool === "windfall-debt") {
      setDebtBalance((d) => Math.max(0, d + choice.cost)); // cost is negative
    } else if (choice.pool === "windfall-goals") {
      setGoalsBalance((g) => g - choice.cost); // cost negative -> adds
    } else if (choice.pool === "windfall-discretionary") {
      setPools((p) => ({ ...p, discretionary: p.discretionary - choice.cost }));
    } else if (choice.pool && choice.cost) {
      setPools((p) => ({ ...p, [choice.pool]: (p[choice.pool] ?? 0) - choice.cost }));
    }

    setResolved({ choice, scenario });
    setLog((prev) => [...prev, { week: scenario.week, title: scenario.title, choice, }]);
  };

  const handleNext = () => {
    setResolved(null);
    setScenarioIndex((i) => i + 1);
  };

  if (allDone) {
    return (
      <div className="animate-popIn text-center">
        <p className="label-pixel mb-4 text-lime">Month Complete</p>
        <button
          onClick={() =>
            onComplete({
              pools,
              emergencyFundBalance,
              debtBalance,
              goalsBalance,
              debtIncurred,
              log,
            })
          }
          className="btn-magenta"
        >
          FILE MISSION REPORT ▶
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Division status HUD */}
      <div className="panel mb-5 space-y-2 p-4">
        <p className="mb-1 font-display text-[9px] text-mist/50">DIVISION STATUS</p>
        <Bar label="Essentials" value={pools.essentials} max={plan.essentials || 1} accent="cyan" />
        <Bar label="Discretionary" value={pools.discretionary} max={plan.discretionary || 1} accent="danger" />
        <Bar label="Sinking Fund" value={pools.sinkingFund} max={plan.sinkingFund || 1} accent="lime" />
        <Bar label="Emergency Fund" value={emergencyFundBalance} max={EMERGENCY_FUND_GOAL} accent="lime" suffix={` / $${EMERGENCY_FUND_GOAL} goal`} />
        <Bar label="Debt Remaining" value={debtBalance} max={STARTING_DEBT} accent="gold" />
      </div>

      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/50">
        <span>{scenario.week}</span>
        <span>
          {scenarioIndex + 1}/{SCENARIOS.length}
        </span>
      </div>

      <div className="animate-popIn mb-5 border-2 border-black bg-ink p-5">
        <p className="pixel-heading mb-3 text-sm text-mist">{scenario.title}</p>
        <p className="text-sm leading-relaxed text-mist/80">{scenario.text}</p>
      </div>

      {!resolved ? (
        <div className="space-y-2">
          {scenario.choices.map((choice) => {
            const affordable = canAfford(choice);
            return (
              <button
                key={choice.id}
                disabled={!affordable}
                onClick={() => applyChoice(choice)}
                className={`w-full border-2 px-4 py-3 text-left text-xs transition-colors ${
                  affordable
                    ? "border-mist/30 text-mist hover:border-cyan hover:text-cyan"
                    : "cursor-not-allowed border-mist/10 text-mist/30"
                }`}
              >
                {choice.label}
                {!affordable && choice.pool && (
                  <span className="ml-2 text-[10px] text-danger">
                    (not enough in {POOL_LABELS[choice.pool] || choice.pool})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div
          className={`animate-popIn border-2 p-4 text-center ${
            resolved.choice.tag === "good"
              ? "border-lime bg-lime/10 text-lime"
              : resolved.choice.tag === "bad"
              ? "border-danger bg-danger/10 text-danger"
              : "border-gold bg-gold/10 text-gold"
          }`}
        >
          <p className="mb-3 text-xs">{resolved.choice.result}</p>
          <button onClick={handleNext} className="btn-cyan !text-[9px]">
            CONTINUE ▶
          </button>
        </div>
      )}
    </div>
  );
}
